<script setup lang="ts">
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRDropdownItem from "../UI/DRDropdownItem.vue";
import { computed, ref } from "vue";
import ConfirmationModal from "../generic/ConfirmationModal.vue";
import { useTranslation } from "i18next-vue";
import type { Diagnostic } from "@/types";
import {
  useDeleteDiagnostic,
  useCreateReport,
  useSetDefaultReport,
  useStartDiagnosticDataExtraction,
} from "@/stores/queries";
import { useDownloadFile } from "@/stores/queries/files.query";
import { getGpsData, googleUrl } from "./utils";
import { useNavigate } from "@/composable/useNavigate";

const { t } = useTranslation();

const props = withDefaults(
  defineProps<{
    diagnostic: Diagnostic;
    size?: number;
    selected?: boolean;
    disableSelect?: boolean;
    showRename?: boolean;
    showReports?: boolean;
    showGoogleMaps?: boolean;
    showEditReport?: boolean;
    showInspection?: boolean;
    /** Ajoute un groupe d'actions sur le rapport d'inspection précédent. */
    showPastReport?: boolean;
  }>(),
  {
    size: 30,
    showRename: false,
    showReports: true,
    showGoogleMaps: true,
    showEditReport: false,
    showInspection: false,
    showPastReport: false,
  }
);

const emits = defineEmits<{
  (e: "deleted"): void;
  (e: "toggle-select"): void;
  (e: "rename"): void;
  /** Demande d'import / remplacement du rapport précédent. */
  (e: "import-report"): void;
}>();

const id = computed(() => props.diagnostic.id);

const { mutateAsync: deleteReportTemplate, isPending: isDeleting } =
  useDeleteDiagnostic(id);

const isLoading = computed(() => isDeleting.value);

const confirmDelete = ref(false);

const handleConfirmDelete = async () => {
  await deleteReportTemplate(undefined, {
    onSuccess: () => {
      confirmDelete.value = false;
      emits("deleted");
    },
  });
};
const diagnosticIdRef = computed(() => props.diagnostic.id);
const { navigateDiagnostic, navigateReport, navigateFieldObservations } =
  useNavigate();

const { mutateAsync: createReport, isPending: isCreatingReport } =
  useCreateReport(diagnosticIdRef);
const { mutateAsync: setDefaultReport } = useSetDefaultReport(diagnosticIdRef);

const handleEditReport = async () => {
  if (props.diagnostic.defaultReportId) {
    navigateReport(props.diagnostic.defaultReportId);
  } else {
    const report = await createReport(undefined);
    await setDefaultReport(report.id);
    navigateReport(report.id);
  }
};

const handleInspection = () => {
  navigateFieldObservations(props.diagnostic.id);
};

// ── Rapport de l'inspection précédente ──────────────────────────────────────
const pastReportFile = computed(() => props.diagnostic.document);

const { mutateAsync: downloadFile, isPending: isDownloading } =
  useDownloadFile();
const { mutateAsync: startAnalysis, isPending: isAnalyzing } =
  useStartDiagnosticDataExtraction(diagnosticIdRef);

const isExtracting = computed(
  () => props.diagnostic.isExtractingData || isAnalyzing.value
);

const handleDownloadPastReport = () => {
  if (pastReportFile.value) downloadFile(pastReportFile.value);
};

const gpsData = computed(() => getGpsData(props.diagnostic));

const openGpsUrl = () => {
  if (gpsData.value) {
    const url = googleUrl(gpsData.value);
    window.open(url, "_blank");
  }
};
</script>

<template>
  <el-dropdown trigger="click" @click.stop.prevent>
    <DRBtnIcon
      :disabled="isLoading"
      icon="bi-three-dots"
      variant="flat"
      :size="size"
      @click.stop.prevent
      @dblclick.stop.prevent
    />

    <template #dropdown>
      <el-dropdown-menu>
        <DRDropdownItem
          v-if="showReports"
          @select="navigateDiagnostic(diagnostic.id)"
          icon="bi-box-arrow-up-right"
          :label="$t('open')"
        />

        <DRDropdownItem
          v-if="showRename"
          @select="$emit('rename')"
          icon="bi-pencil"
          :label="$t('rename')"
        />

        <DRDropdownItem
          v-if="showInspection"
          @select="handleInspection"
          icon="bi-clipboard2-pulse"
          :label="t('report:diagnostics.goToInspection')"
        />

        <DRDropdownItem
          v-if="showEditReport"
          @select="handleEditReport"
          icon="bi-file-earmark-text"
          :loading="isCreatingReport"
          :label="t('report:diagnostics.editReport')"
        />

        <DRDropdownItem
          v-if="showGoogleMaps && gpsData"
          @select="openGpsUrl()"
          icon="bi-map"
          :label="$t('report:diagnostics.access.link.googlemaps')"
        />

        <!-- Rapport de l'inspection précédente -->
        <DRDropdownItem
          v-if="showPastReport && pastReportFile"
          divided
          @select="handleDownloadPastReport()"
          icon="bi-download"
          :loading="isDownloading"
          :label="t('report:diagnostics.downloadPastReport')"
        />
        <DRDropdownItem
          v-if="showPastReport && pastReportFile"
          @select="startAnalysis()"
          icon="bi-stars"
          :disabled="isExtracting"
          :label="t('report:diagnostics.extracting.button')"
        />
        <DRDropdownItem
          v-if="showPastReport"
          :divided="!pastReportFile"
          @select="$emit('import-report')"
          icon="bi-upload"
          :label="
            t(
              pastReportFile
                ? 'report:diagnostics.replacePastReport'
                : 'report:diagnostics.importPastReport'
            )
          "
        />

        <DRDropdownItem
          divided
          @select="confirmDelete = true"
          icon="bi-trash3"
          :label="$t('dashboard:diagnostic.actions.delete.label')"
          type="danger"
        />

        <ConfirmationModal
          v-model="confirmDelete"
          @confirm="handleConfirmDelete()"
          @cancel="confirmDelete = false"
          :label="$t('dashboard:diagnostic.actions.delete.dialog.title')"
          :confirm-label="$t('delete')"
          danger
          :message="
            $t('dashboard:diagnostic.actions.delete.dialog.message', {
              name: diagnostic.name,
            })
          "
        />
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<style lang="scss" scoped></style>
